import { useExamCenter } from '@exam-center/exam-center-details';
import {
  ExamCenterProctorType,
  useCountActiveExamCenterProctorsByTypeQuery,
  useCountExamCenterRoomsByProvinceQuery,
} from '@exam-center/graphql';
import {
  PaperSection,
  Flex,
  GroupOutlinedIcon,
  PlaceOutlinedIcon,
  type PaperSectionProps,
  TotalSummaryCard,
} from '@shared/components';
import { formatNumber } from '@shared/utils';
import { useMemo } from 'react';

import { mapExamCenterRoomsCountByProvinceData, mapProctorsCountByTypeData } from './utils';

type LocationAndProctorStatsSectionProps = Omit<PaperSectionProps, 'title'>;

export default function LocationAndProctorStatsSection(
  props: Readonly<LocationAndProctorStatsSectionProps>,
) {
  const { examCenter } = useExamCenter();

  const { data: countActiveExamCenterProctorsResult, loading: isCountingActiveExamCenterProctors } =
    useCountActiveExamCenterProctorsByTypeQuery({
      variables: {
        countActiveExamCenterProctorsByTypeInput: {
          examCenterCode: examCenter.code,
        },
      },
      skip: !examCenter.code,
    });

  const { proctorsCountByType, totalProctorsCount } = useMemo(() => {
    return mapProctorsCountByTypeData(
      countActiveExamCenterProctorsResult?.ecCountActiveExamCenterProctorsByType
        .proctorsCountByType ?? {},
    );
  }, [countActiveExamCenterProctorsResult]);

  const {
    data: countExamCenterRoomsByProvinceResult,
    loading: isCountingExamCenterRoomsByProvince,
  } = useCountExamCenterRoomsByProvinceQuery({
    variables: {
      countExamCenterRoomsByProvinceInput: {
        examCenterCode: examCenter.code,
      },
    },
    skip: !examCenter.code,
  });

  const { bangkokAndOtherProvinceRoomsCount, totalRoomsCount } = useMemo(() => {
    return mapExamCenterRoomsCountByProvinceData(
      countExamCenterRoomsByProvinceResult?.ecCountExamCenterRoomsByProvince
        .examCenterRoomsCountByProvince ?? {},
    );
  }, [countExamCenterRoomsByProvinceResult]);

  return (
    <PaperSection title='ผู้คุมสอบและสถานที่สอบทั้งหมด' {...props}>
      <Flex
        sx={() => ({
          flexDirection: 'column',
          padding: (theme) => theme.spacing(6),
          gap: (theme) => theme.spacing(6),
        })}
      >
        <TotalSummaryCard
          icon={<GroupOutlinedIcon color='primary' fontSize='large' />}
          slotProps={{
            iconContainer: {
              sx: (theme) => ({ backgroundColor: theme.palette.primary.lighter }),
            },
          }}
          loading={isCountingActiveExamCenterProctors}
          label='จำนวนผู้คุมสอบทั้งหมด'
          value={`${formatNumber(totalProctorsCount)} คน`}
          statItems={[
            {
              label: 'ประเภทที่ 1',
              value: formatNumber(proctorsCountByType[ExamCenterProctorType.Proctor_1]),
            },
            {
              label: 'ประเภทที่ 2',
              value: formatNumber(proctorsCountByType[ExamCenterProctorType.Proctor_2]),
            },
            {
              label: 'ประเภทที่ 3',
              value: formatNumber(proctorsCountByType[ExamCenterProctorType.Proctor_3]),
            },
          ]}
        />
        <TotalSummaryCard
          icon={<PlaceOutlinedIcon color='primary' fontSize='large' />}
          slotProps={{
            iconContainer: {
              sx: (theme) => ({ backgroundColor: theme.palette.primary.lighter }),
            },
          }}
          loading={isCountingExamCenterRoomsByProvince}
          label='จำนวนห้องสอบทั้งหมด'
          value={`${formatNumber(totalRoomsCount)} ห้อง`}
          statItems={[
            {
              label: 'กรุงเทพฯ',
              value: formatNumber(bangkokAndOtherProvinceRoomsCount.bangkok),
            },
            {
              label: 'ต่างจังหวัด',
              value: formatNumber(bangkokAndOtherProvinceRoomsCount.other),
            },
          ]}
        />
      </Flex>
    </PaperSection>
  );
}
